/*
 * Alorae <mini-cart> Web Component.
 * Replacement for legacy `theme.CartDrawer` (assets/theme.js ~line 4008) and
 * the inline markup in snippets/cart-drawer.liquid.
 *
 * Subscribes to the singleton cart in @/components/cart, re-renders its line
 * items list in place on every cart event, and manages drawer open/close
 * (focus trap, Escape key, overlay click, document-level mini-cart:open).
 *
 * Wire up:
 *   {% render 'mini-cart' %}            in layout/theme.liquid
 *   document.dispatchEvent(new CustomEvent('mini-cart:open'))   from anywhere
 *
 * See docs/alorae-rebuild/decompose-theme-files-roadmap.md §Phase 4E.
 */

import { Component } from '@/component';
import { cart } from '@/components/cart';
import { formatMoney } from '@/components/currency';

/**
 * Selectors that identify focusable descendants inside the drawer. Used by the
 * focus trap when the drawer is open.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Escape user-supplied strings before they're inserted into the DOM via
 * innerHTML. Cart line items contain merchant/product-supplied titles.
 *
 * @param {string} value
 * @returns {string}
 */
function escapeHTML(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build the markup for a single line item. Kept as a pure function so it can be
 * unit-tested in isolation and so the render loop is allocation-light.
 *
 * @param {object} item Shopify line item.
 * @returns {string}
 */
function renderLineItem(item) {
  const key = escapeHTML(item.key);
  const title = escapeHTML(item.product_title || item.title || '');
  const variant = item.variant_title && item.variant_title !== 'Default Title'
    ? `<div class="mini-cart__variant">${escapeHTML(item.variant_title)}</div>`
    : '';
  const image = item.image
    ? `<img class="mini-cart__image" src="${escapeHTML(item.image)}" alt="${escapeHTML(item.product_title || '')}" width="64" height="64" loading="lazy">`
    : '<div class="mini-cart__image mini-cart__image--placeholder" aria-hidden="true"></div>';
  const url = item.url ? escapeHTML(item.url) : '#';
  const price = formatMoney(item.final_line_price ?? item.line_price ?? item.final_price * item.quantity);
  const quantity = Number(item.quantity) || 0;

  return `
    <li class="mini-cart__item" data-line-key="${key}">
      <a href="${url}" class="mini-cart__item-image" tabindex="-1" aria-hidden="true">${image}</a>
      <div class="mini-cart__item-body">
        <a href="${url}" class="mini-cart__item-title">${title}</a>
        ${variant}
        <div class="mini-cart__item-controls">
          <div class="mini-cart__qty" role="group" aria-label="Quantity">
            <button type="button" class="mini-cart__qty-btn" data-action="qty-down" aria-label="Decrease quantity">&minus;</button>
            <span class="mini-cart__qty-value" data-ref-qty>${quantity}</span>
            <button type="button" class="mini-cart__qty-btn" data-action="qty-up" aria-label="Increase quantity">&plus;</button>
          </div>
          <button type="button" class="mini-cart__remove" data-action="remove" aria-label="Remove ${title}">Remove</button>
        </div>
      </div>
      <div class="mini-cart__item-price">${price}</div>
    </li>
  `;
}

/**
 * <mini-cart> — slide-in cart drawer.
 *
 * Markup contract (rendered by snippets/mini-cart.liquid):
 *   [data-ref="root"]         visible drawer container
 *   [data-ref="overlay"]      backdrop
 *   [data-ref="items"]        line items <ul>
 *   [data-ref="empty"]        empty-state region
 *   [data-ref="total"]        subtotal element
 *   [data-ref="count"]        item count badge
 *   [data-ref="progress"]     free-ship progress bar fill
 *   [data-ref="progress-msg"] free-ship message
 *   [data-ref="close"]        close button
 *
 * Attributes:
 *   data-free-shipping-threshold  Free-shipping threshold in cents (optional).
 */
export class MiniCart extends Component {
  constructor() {
    super();
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCartItemAdded = this._onCartItemAdded.bind(this);
    this._onCartItemRemoved = this._onCartItemRemoved.bind(this);
    this._onCartError = this._onCartError.bind(this);
    this._onDocOpen = () => this.open();
    this._onDocClose = () => this.close();
    this._onKeydown = this._onKeydown.bind(this);
    this._onClick = this._onClick.bind(this);
    /** @type {Element | null} Element focused before opening — restored on close. */
    this._lastActive = null;
  }

  /* ------------------------------------------------------------------ */
  /* Lifecycle                                                          */
  /* ------------------------------------------------------------------ */

  init() {
    // Cart singleton — every cart mutation re-renders the drawer body.
    this.addListener(cart, 'cart:updated', this._onCartUpdated);
    this.addListener(cart, 'cart:item-added', this._onCartItemAdded);
    this.addListener(cart, 'cart:item-removed', this._onCartItemRemoved);
    this.addListener(cart, 'cart:error', this._onCartError);

    // Public open/close API — any consumer can dispatch on `document`.
    this.addListener(document, 'mini-cart:open', this._onDocOpen);
    this.addListener(document, 'mini-cart:close', this._onDocClose);

    // Drawer-local interaction handlers.
    this.addListener(this, 'click', this._onClick);
    if (this.refs.close) {
      this.addListener(this.refs.close, 'click', this._onDocClose);
    }
    if (this.refs.overlay) {
      this.addListener(this.refs.overlay, 'click', this._onDocClose);
    }

    // Hydrate from any prefetched cart state, then refresh from network so the
    // SSR initial render is reconciled against the live cart.
    if (cart.getState()) {
      this._render(cart.getState());
    }
    cart.refresh().catch(() => {
      /* Errors already surfaced via cart:error. */
    });
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                         */
  /* ------------------------------------------------------------------ */

  /** Open the drawer and focus the first focusable element inside it. */
  open() {
    if (this.classList.contains('is-open')) return;
    this._lastActive = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('open', '');
    if (this.refs.root) this.refs.root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mini-cart-open');
    // Esc + tab-trap only attached while open — keeps key handling cheap.
    this.addListener(document, 'keydown', this._onKeydown);
    // Defer focus until the slide-in transition starts so the browser doesn't
    // scroll the page to surface the focused element.
    requestAnimationFrame(() => this._focusFirst());
    this.dispatch('mini-cart:opened');
  }

  /** Close the drawer and restore focus to whatever opened it. */
  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.removeAttribute('open');
    if (this.refs.root) this.refs.root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mini-cart-open');
    // Drop the keydown listener — addListener registered it for auto-cleanup,
    // but we strip it immediately to avoid handling Tab while closed.
    document.removeEventListener('keydown', this._onKeydown);
    this._listeners = this._listeners.filter((entry) => entry.handler !== this._onKeydown);
    if (this._lastActive && typeof this._lastActive.focus === 'function') {
      try {
        this._lastActive.focus();
      } catch (_) {
        /* element may have been removed from the DOM — ignore */
      }
    }
    this._lastActive = null;
    this.dispatch('mini-cart:closed');
  }

  /** Toggle open/closed. */
  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /* ------------------------------------------------------------------ */
  /* Cart event handlers                                                */
  /* ------------------------------------------------------------------ */

  _onCartUpdated(event) {
    this._render(event.detail && event.detail.cart);
  }

  _onCartItemAdded(event) {
    this._render(event.detail && event.detail.cart);
    this.open();
  }

  _onCartItemRemoved(event) {
    this._render(event.detail && event.detail.cart);
  }

  _onCartError(event) {
    const error = event.detail && event.detail.error;
    // Keep the existing markup; surface a non-blocking message in the empty
    // region so the user sees something went wrong.
    if (this.refs.empty && error) {
      const msg = document.createElement('p');
      msg.className = 'mini-cart__error';
      msg.setAttribute('role', 'alert');
      msg.textContent = error.message || 'Something went wrong updating your cart.';
      // Replace any prior error message but leave the empty-state copy intact.
      const prev = this.refs.empty.querySelector('.mini-cart__error');
      if (prev) prev.remove();
      this.refs.empty.appendChild(msg);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Click delegation (qty +/-, remove)                                 */
  /* ------------------------------------------------------------------ */

  _onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const actionEl = target.closest('[data-action]');
    if (!actionEl || !this.contains(actionEl)) return;

    const action = actionEl.getAttribute('data-action');
    const lineEl = actionEl.closest('[data-line-key]');
    const lineKey = lineEl ? lineEl.getAttribute('data-line-key') : null;
    if (!lineKey) return;

    event.preventDefault();

    const current = this._findLine(lineKey);
    const qty = current ? Number(current.quantity) || 0 : 0;

    if (action === 'remove') {
      cart.removeItem(lineKey).catch(() => {});
      return;
    }
    if (action === 'qty-up') {
      cart.updateItem(lineKey, qty + 1).catch(() => {});
      return;
    }
    if (action === 'qty-down') {
      cart.updateItem(lineKey, Math.max(0, qty - 1)).catch(() => {});
    }
  }

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Re-render the in-place regions: items list, totals, count, free-ship bar,
   * empty/items visibility. Leaves the surrounding chrome (header, close
   * button, checkout form) untouched.
   *
   * @param {object|null} cartState
   */
  _render(cartState) {
    if (!cartState) return;

    const items = Array.isArray(cartState.items) ? cartState.items : [];
    const count = Number(cartState.item_count) || 0;
    const subtotal = Number(
      cartState.items_subtotal_price ?? cartState.total_price ?? 0
    );

    // Items vs empty state.
    if (this.refs.items) {
      if (items.length === 0) {
        this.refs.items.innerHTML = '';
        this.refs.items.hidden = true;
      } else {
        this.refs.items.hidden = false;
        this.refs.items.innerHTML = items.map(renderLineItem).join('');
      }
    }
    if (this.refs.empty) {
      this.refs.empty.hidden = items.length !== 0;
      // Clear any stale error banner once the cart is healthy again.
      if (items.length !== 0) {
        const err = this.refs.empty.querySelector('.mini-cart__error');
        if (err) err.remove();
      }
    }

    // Totals & count badge.
    if (this.refs.total) this.refs.total.textContent = formatMoney(subtotal);
    if (this.refs.count) {
      this.refs.count.textContent = String(count);
      this.refs.count.hidden = count === 0;
    }

    this._renderProgress(subtotal);
  }

  /**
   * Update the free-shipping progress bar fill (0-100%) and the supporting
   * message. No-ops if the threshold attribute is unset or not a positive
   * integer.
   *
   * @param {number} subtotal Cart subtotal in cents.
   */
  _renderProgress(subtotal) {
    const thresholdRaw = this.dataset.freeShippingThreshold;
    const threshold = Number(thresholdRaw);
    if (!thresholdRaw || !Number.isFinite(threshold) || threshold <= 0) {
      // No threshold configured — hide both refs if present.
      if (this.refs.progress) this.refs.progress.style.width = '0%';
      if (this.refs['progress-msg']) this.refs['progress-msg'].textContent = '';
      return;
    }

    const remaining = Math.max(0, threshold - subtotal);
    const pct = Math.min(100, Math.round((subtotal / threshold) * 100));

    if (this.refs.progress) {
      this.refs.progress.style.width = `${pct}%`;
      this.refs.progress.setAttribute('aria-valuenow', String(pct));
    }
    if (this.refs['progress-msg']) {
      if (remaining === 0) {
        this.refs['progress-msg'].textContent = 'You qualify for free shipping!';
      } else {
        this.refs['progress-msg'].textContent = `Add ${formatMoney(remaining)} more for free shipping`;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Internals                                                          */
  /* ------------------------------------------------------------------ */

  /**
   * Find a line item in the cached cart state by key.
   * @param {string} lineKey
   * @returns {object|null}
   */
  _findLine(lineKey) {
    const state = cart.getState();
    if (!state || !Array.isArray(state.items)) return null;
    return state.items.find((line) => line.key === lineKey) || null;
  }

  /**
   * Keydown handler attached only while the drawer is open. Implements Escape
   * to close and a Tab/Shift-Tab focus trap.
   *
   * @param {KeyboardEvent} event
   */
  _onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this._focusable();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /** @returns {HTMLElement[]} */
  _focusable() {
    const scope = this.refs.root || this;
    return Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
      // Filter out hidden elements (`hidden` attribute or `display: none`).
      if (el.hasAttribute('hidden')) return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') {
        return false;
      }
      return true;
    });
  }

  _focusFirst() {
    const focusable = this._focusable();
    if (focusable.length > 0) {
      try {
        focusable[0].focus();
      } catch (_) {
        /* ignore */
      }
    }
  }
}

Component.define('mini-cart', MiniCart);

export default MiniCart;
